import React from 'react';
import { ArrowRight, Heart, Clock, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const realStories = [
  {
    name: 'Emma',
    business: 'Bakery Owner',
    location: 'Manchester, UK',
    struggle: 'Emma was getting up at 4am to bake, then spending her afternoons taking phone orders and managing deliveries. She hadn\'t had a proper weekend in two years.',
    solution: 'WhatsApp bot takes orders, manages delivery schedules, and sends customers updates about their fresh bread and pastries.',
    results: [
      { icon: Heart, value: 'Weekends back', label: 'First time in 2 years' },
      { icon: TrendingUp, value: '40% more orders', label: 'Never missing customers' },
      { icon: Clock, value: '6 hours saved', label: 'Every single day' }
    ],
    quote: "I actually went to my daughter's football match last Saturday. The business kept running while I was cheering from the sidelines.",
    emotion: 'From exhausted to present mum',
    image: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&w=800&q=80'
  },
  {
    name: 'James',
    business: 'Plumbing Service',
    location: 'Birmingham, UK',
    struggle: 'James was losing emergency calls because he couldn\'t answer his phone while fixing pipes. His wife was tired of being his unpaid receptionist.',
    solution: 'Voice AI answers calls, books emergency appointments, and only interrupts James for genuine emergencies.',
    results: [
      { icon: Clock, value: '24/7 availability', label: 'Never miss emergencies' },
      { icon: Heart, value: 'Happy wife', label: 'No more receptionist duty' },
      { icon: TrendingUp, value: '£3,000 more', label: 'Monthly revenue' }
    ],
    quote: "My wife said 'thank you' when I got home yesterday. She hasn't had to answer business calls in weeks.",
    emotion: 'From family tension to harmony',
    image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=800&q=80'
  },
  {
    name: 'Priya',
    business: 'Accounting Firm',
    location: 'London, UK',
    struggle: 'Priya was working until 11pm every night during tax season, manually processing client documents and sending updates. Her kids barely saw her.',
    solution: 'Automation processes documents, updates client records, and sends progress reports automatically.',
    results: [
      { icon: Clock, value: 'Home by 6pm', label: 'Even during tax season' },
      { icon: TrendingUp, value: '3x faster', label: 'Document processing' },
      { icon: Heart, value: 'Bedtime stories', label: 'Every night again' }
    ],
    quote: "I read my son a bedtime story last night. He said 'Mummy, you're not tired anymore!' It broke my heart and made me so happy.",
    emotion: 'From missing childhood to being present',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=800&q=80'
  }
];

export default function ClientSuccess() {
  return (
    <section className="py-32 bg-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-[0.02]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-20">
          <h2 className="text-5xl font-bold mb-6">
            <span className="text-gradient">Real Business Owners, Real Freedom</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            These aren't corporate case studies. These are real people who stopped working nights and weekends and started living again.
          </p>
        </div>

        <div className="space-y-20 mb-20">
          {realStories.map((story, index) => (
            <div key={index} className="group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-teal-500 to-purple-500 rounded-xl blur opacity-25 group-hover:opacity-30 transition duration-300" />
              <div className="relative bg-white rounded-xl p-8 transition-all duration-300 hover:translate-y-[-2px] shadow-lg">
                <div className="grid lg:grid-cols-2 gap-12">
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <img
                        src={story.image}
                        alt={story.name}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-1">{story.name}</h3>
                        <p className="text-teal-600 font-medium">{story.business}</p>
                        <p className="text-gray-500 text-sm">{story.location}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                        <h4 className="text-sm font-semibold text-red-600 mb-2">The Daily Struggle:</h4>
                        <p className="text-gray-700">{story.struggle}</p>
                      </div>
                      
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h4 className="text-sm font-semibold text-blue-600 mb-2">How We Helped:</h4>
                        <p className="text-gray-700">{story.solution}</p>
                      </div>
                    </div>

                    <blockquote className="border-l-4 border-teal-500 pl-4 italic text-gray-700 bg-gray-50 p-4 rounded-r-lg">
                      "{story.quote}"
                    </blockquote>

                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                      <h4 className="text-sm font-semibold text-purple-600 mb-2">Life Transformation:</h4>
                      <p className="text-gray-700 font-medium italic">{story.emotion}</p>
                    </div>
                  </div>

                  <div className="relative">
                    <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                      <h4 className="text-xl font-bold text-gray-900 mb-6">What Changed</h4>
                      <div className="space-y-6">
                        {story.results.map((result, i) => (
                          <div key={i} className="flex items-center space-x-4">
                            <div className="w-12 h-12 rounded-lg bg-green-500/10 p-2.5 flex items-center justify-center">
                              <result.icon className="h-6 w-6 text-green-600" />
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-green-700">{result.value}</div>
                              <div className="text-sm text-green-600">{result.label}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <div className="max-w-3xl mx-auto mb-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Your Story Could Be Next</h3>
            <p className="text-lg text-gray-600">
              Stop sacrificing your personal life for your business. These transformations happen in weeks, not years. 
              Let's have a conversation about what freedom looks like for you.
            </p>
          </div>
          
          <Link 
            to="/contact" 
            className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-md text-white hover:text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <span>Start Your Transformation</span>
            <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}